import React, { useMemo, useState } from 'react'
import { safeParseFinancialBreakdown } from '../schemas/analysisResult.zod'

function formatMoneyAnnual(v) {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  const sign = n < 0 ? '-' : ''
  return `${sign}£${Math.abs(Math.round(n)).toLocaleString()}`
}

function formatMoneyMonthly(v) {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return ''
  const n = Number(v)
  const sign = n < 0 ? '-' : ''
  return `${sign}£${Math.abs(Math.round(n)).toLocaleString()}/mo`
}

/**
 * Expandable P&L-style table from backend StrategyFinancialBreakdown.
 */
export default function CashflowBreakdown({ breakdown, compact = false }) {
  const [open, setOpen] = useState(false)
  if (!breakdown || typeof breakdown !== 'object') return null

  const parsed = useMemo(() => safeParseFinancialBreakdown(breakdown), [breakdown])
  const bd = parsed.success ? parsed.data : breakdown
  if (import.meta.env.DEV && !parsed.success) {
    console.warn('[CashflowBreakdown] financial_breakdown failed schema check:', parsed.error?.flatten?.())
  }

  const items = Array.isArray(bd.line_items) ? bd.line_items : []

  return (
    <div className={`${compact ? 'mt-2' : 'mt-3'} border-t border-gray-200/60 pt-2`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-semibold text-gray-700 hover:text-gray-900 flex items-center gap-1"
      >
        <span aria-hidden>{open ? '▼' : '▶'}</span>
        Cashflow breakdown (audited lines)
      </button>
        {!compact && (
        <p className="text-[11px] text-gray-500 mt-1">
          Cash-on-cash (Y1, pre-tax){' '}
          <strong>{Number(bd.cash_on_cash_year1_pre_tax_pct ?? 0).toFixed(2)}%</strong>
          {' · '}basis: annual pre-tax cashflow ÷ cash to complete (
          <strong>£{Number(bd.cash_to_complete ?? 0).toLocaleString()}</strong>).{' '}
          {bd.mortgage_interest_only === false ? (
            <>Mortgage illustrated as repayment.</>
          ) : (
            <>Interest-only</>
          )}{' '}
          {bd.mortgage_rate_annual_nominal != null
            ? `${(Number(bd.mortgage_rate_annual_nominal) * 100).toFixed(2)}%`
            : '—'}{' '}
          p.a., LTV {(Number(bd.ltv ?? 0) * 100).toFixed(0)}%.
        </p>
      )}
      {open && (
        <div className="mt-2 overflow-x-auto rounded border border-gray-100 bg-white/80">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="py-1.5 px-2 font-medium">Line</th>
                <th className="py-1.5 px-2 font-medium text-right">£ / year</th>
                <th className="py-1.5 px-2 font-medium text-right hidden sm:table-cell">£ / month</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.key} className="border-b border-gray-50">
                  <td className="py-1.5 px-2 text-gray-800">
                    {row.label}
                    {row.notes ? (
                      <span className="block text-[10px] text-gray-400 font-normal">{row.notes}</span>
                    ) : null}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums">
                    {formatMoneyAnnual(row.amount_annual)}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums text-gray-500 hidden sm:table-cell">
                    {row.amount_monthly != null ? formatMoneyMonthly(row.amount_monthly) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
